import CustomerCrossReference from "@dana/Components/CustomerCrossReference";
import ReplacementPart from "@dana/Components/ReplacementPart";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { css } from "styled-components";



interface AutoCompleteProps {
    products?: ProductAutocompleteItemModel[];
    search: string;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

type OwnProps = AutoCompleteProps;

const mapStateToProps = (state: ApplicationState, _ownProps: OwnProps) => ({
    danaGeneralSettings: getDanaGeneralSettings(state),
});


const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

type Props = OwnProps & ReduxProps;

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    obsoleteTitleWrapper?: InjectableCss;
    obsoleteTitle?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: ${themeBackgrounds.lightGray};
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        underline: false,
        css: css`
            font-weight: ${fontWeights.semiBold};
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;

            span {
                text-decoration: none;
            }
        `,
    },
    obsoleteTitleWrapper: {
        css: css`
            background: ${themeBackgrounds.badgeYellow};
            padding: 0 4px;

            > ${TypographyStyle} {
                font-size: ${fontSizes.xSmall} !important;
                line-height: ${lineHeights.default} !important;
            }
        `,
    },
    obsoleteTitle: {
        size: fontSizes.xSmall,
        lineHeight: lineHeights.default,
        color: themeColors.danger,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    showDiscontinuedLabel = this.props.danaGeneralSettings?.partsObsoleteDiscontinuedStatusCode
        ?.toLowerCase()
        .split(",")
        ?.map((s: any) => s.trim());

    render() {
        const { products } = this.props;
        const styles = this.styles;

        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography>
                {products &&
                    products.map(product => (
                        <GridContainer
                            {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                            key={product.id!}
                            onClick={() => {
                                const searchValue = this.props.search?.toLowerCase() ?? "";
                                const manufacturerItemMatch =
                                    product.manufacturerItemNumber?.toLowerCase() === searchValue;

                                let nameMatch = false;
                                let otherPartInterChangeMatch = false;
                                if (product.isNameCustomerOverride) {
                                    const nameList = (product.name ?? "").split(",").map(x => x.trim().toLowerCase());
                                    nameMatch = nameList.includes(searchValue);
                                }
                                const otherInterchangeList = (product.properties?.partInterchange ?? "").split(",").map(x => x.trim().toLowerCase().replace(/\s+/g, "-"));
                                if (otherInterchangeList) {
                                    otherPartInterChangeMatch = otherInterchangeList.includes(searchValue);
                                }
                                if (manufacturerItemMatch || nameMatch || otherPartInterChangeMatch) {
                                    this.props.goToUrl(`${product.url}?crossReferance=${this.props.search}`);
                                } else {
                                    this.props.goToUrl(`${product.url}`);
                                }
                              

                            }}
                            className="autocomplete-products-container"
                        >
                            <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                                <LazyImage
                                    {...styles.image}
                                    src={product.image}
                                    className="autocomplete-products-image"
                                />
                            </GridItem>
                            <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                {product?.properties?.danaStatusCode && this.showDiscontinuedLabel?.includes(
                                    product?.properties?.danaStatusCode?.toLowerCase(),
                                ) && (
                                        <StyledWrapper {...styles.obsoleteTitleWrapper} className="text-body-xs">
                                            <Typography {...styles.obsoleteTitle}>
                                                {translate("Obsolete/Discontinued")}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                <Link
                                    {...styles.erpNumberText}
                                    data-test-selector="autocompleteProducts_PartNumber"
                                    className="autocomplete-products-erp-number text-body-lg"
                                >
                                    <>{product.displayErpNumber}</>
                                </Link>
                                <Typography
                                    {...styles.titleLink}
                                    data-test-selector={`productId_${product.id}`}
                                    className="autocomplete-products-link"
                                >
                                    {product.displayTitle}
                                    {product.manufacturerItemNumber?.toLowerCase() === this.props.search?.toLowerCase() ? (
                                        <>
                                            <CustomerCrossReference product={product}  CustomerPartNumber={product?.manufacturerItemNumber} />
                                        </>
                                    ) : (
                                        (() => {
                                            const searchValue = this.props.search?.toLowerCase() ?? "";

                                            const matchesName =
                                                product.isNameCustomerOverride &&
                                                (product.name ?? "").split(",").map(x => x.trim().toLowerCase()).includes(searchValue);

                                            const matchesInterchange =
                                                !!this.props.search &&
                                                (product.properties?.partInterchange ?? "")
                                                    .split(",")
                                                    .map(x => x.trim().toLowerCase().replace(/\s+/g, "-"))
                                                    .includes(searchValue);
                                            const matchesReplacement = product.properties?.replacementPartNumber ?? "";
                                            if (matchesReplacement) {
                                                return <ReplacementPart ReplacementPartNumber={product.properties?.replacementPartNumber} />
                                            }

                                            if (matchesName) {
                                                return <CustomerCrossReference product={product} CustomerPartNumber={this.props.search} />
                                            }
                                            if (matchesInterchange) {
                                                return (
                                                    <>
                                                        <CustomerCrossReference product={product} CustomerPartNumber={this.props.search} />
                                                    </>
                                                );
                                            }


                                            return null;
                                        })()
                                    )}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    ))}
                {!products && (
                    // eslint-disable-next-line react/jsx-no-undef
                    <StyledWrapper className="autocomplete-column-wrapper text-body-sm">
                        {translate("No matching results.")}
                    </StyledWrapper>
                )}
            </>
        );
    }
}

export default connector(AutocompleteProducts);
